"use client";

import { motion } from "framer-motion";
import Icon from "@/components/ui/AppIcon";

const steps = [
  {
    number: "01",
    icon: "PhoneIcon",
    title: "Book a Free Strategy Call",
    desc: "We start with a 30-minute call to understand your product, volume, and current logistics setup. No sales pressure — just a genuine audit of how Helium can help.",
    detail: "Our team will review your current 3PL, shipping costs, delivery times, and pain points. You'll leave the call with a clear picture of what's possible.",
    timeline: "Day 0",
    color: "bg-helium-500",
    lightColor: "bg-helium-50",
    textColor: "text-helium-500",
  },
  {
    number: "02",
    icon: "DocumentCheckIcon",
    title: "Onboard in 48 Hours",
    desc: "Once you decide to proceed, we set up your account, connect your store, and configure your packaging preferences. Most clients are live within 48 hours.",
    detail: "You receive your dashboard login, integration guides, and your dedicated account manager's direct contact. We handle the tech setup — you just approve it.",
    timeline: "Day 1–2",
    color: "bg-blue-500",
    lightColor: "bg-blue-50",
    textColor: "text-blue-500",
  },
  {
    number: "03",
    icon: "CubeIcon",
    title: "Send Your Stock",
    desc: "Ship your existing inventory to our Shenzhen warehouse — or let us source new stock directly from local factories. We receive, inspect, and catalog everything.",
    detail: "We send you a detailed receiving report within 24 hours of your stock arriving. Every unit is counted, photographed, and entered into your live inventory dashboard.",
    timeline: "Day 2–7",
    color: "bg-purple-500",
    lightColor: "bg-purple-50",
    textColor: "text-purple-500",
  },
  {
    number: "04",
    icon: "TruckIcon",
    title: "Orders Fulfill Automatically",
    desc: "From this point, Helium runs on autopilot. Orders sync from your store, we pick, pack, and dispatch — your customers get their orders in 5–8 days.",
    detail: "You get real-time tracking on every shipment, automated customer notifications, and a monthly performance report with fulfillment metrics and cost breakdown.",
    timeline: "Day 7+",
    color: "bg-green-500",
    lightColor: "bg-green-50",
    textColor: "text-green-500",
  },
];

export default function ProcessTimeline() {
  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: {
        staggerChildren: 0.15
      }
    },
    viewport: { once: true, margin: "-100px" }
  };

  const stepVariant = {
    initial: { opacity: 0, y: 50, scale: 0.9 },
    whileInView: { opacity: 1, y: 0, scale: 1 },
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  };

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-section-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Steps grid - Responsive */}
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 mb-16 sm:mb-20"
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true, margin: "-100px" }}
        >
          {steps?.map((step, index) => (
            <motion.div
              key={step?.number}
              className="glass-card rounded-3xl sm:rounded-4xl p-6 sm:p-8 border border-glass-border backdrop-blur-xl transition-all duration-500 hover:shadow-glass-hover hover:-translate-y-1"
              variants={stepVariant}
            >
              <div className="flex items-center justify-between mb-4 sm:mb-5">
                <span className="text-4xl sm:text-5xl font-extrabold font-heading text-accent-primary/20">
                  {step?.number}
                </span>
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-accent-primary/10 border border-accent-primary/20 flex items-center justify-center">
                  <Icon name={step?.icon} size={24} className="text-accent-primary" />
                </div>
              </div>
              <h3 className="text-lg sm:text-xl font-extrabold font-heading text-text-primary mb-2 sm:mb-3">
                {step?.title}
              </h3>
              <p className="text-xs sm:text-sm text-text-secondary leading-relaxed mb-3 sm:mb-4">
                {step?.desc}
              </p>
              <div className="pt-3 sm:pt-4 border-t border-divider">
                <p className="text-xs text-accent-primary font-semibold">
                  {step?.timeline}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}